import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/blog/SiteHeader";
import { ReviewForm } from "@/components/ReviewForm";
import { cn } from "@/lib/utils";

const TITLE = "Website Concierge — Welcome & Information";
const DESCRIPTION =
  "Welcome information for new Website Concierge clients at Three Day Launch.";

const sections = [
  { id: "sharing-your-website", label: "Sharing Your New Website" },
  { id: "20-places", label: "20 Places to Show Off Your Website" },
  { id: "requesting-updates", label: "Requesting Website Updates" },
  { id: "adding-blog-posts", label: "Adding Blog Posts to Your Website" },
  { id: "seo-blog-review", label: "Monthly SEO Blog Post" },
  { id: "website-analytics-dashboard", label: "Website Analytics Dashboard" },
  { id: "free-website-care", label: "Free Website Care" },
  { id: "leave-a-review", label: "A Favor to Ask: Share Your Experience" },
];

export const Route = createFileRoute("/website-concierge-welcome-information")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: WelcomePage,
});

function WelcomePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader hideBookCall />

      <main className="mx-auto max-w-7xl px-6 py-16 md:py-20">
        {/* Top intro */}
        <header className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">
            Website Concierge
          </p>
          <h1 className="mt-3 text-4xl md:text-5xl font-bold leading-tight">
            Welcome & Information
          </h1>
          <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
            <p>
              Welcome to your Website Concierge information! First things first:{" "}
              <strong className="text-foreground">
                you do not need to review all of this information right away.
              </strong>{" "}
              I'll be sharing the same information with you via email over the
              next several days, one topic at a time, so nothing here is urgent.
            </p>
            <p>
              This page is simply here in case you'd like all of the details up
              front — a one-stop reference you can come back to any time. Use
              the menu on the left (or below on mobile) to jump to whichever
              section is helpful right now.
            </p>
          </div>
        </header>

        <div className="mt-12 grid gap-10 lg:grid-cols-[260px_1fr]">
          {/* Sidebar menu */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <nav aria-label="Page sections" className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                On this page
              </p>
              <ul className="mt-3 space-y-1">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="block rounded-md px-3 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-foreground transition-colors"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Content */}
          <div className="max-w-3xl space-y-16">
            <Section id="sharing-your-website" title="Sharing Your New Website" index={0}>
              <p>{"\n"}</p>
              <p>
                One of the most exciting things about your new website is sharing it!
              </p>
              <p>
                <a
                  href="https://www.loom.com/share/6cb9e61f990548129dfd05cc30dbccb6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-primary"
                >
                  Start with my video on your launch graphics and launch promo
                  templates
                </a>
                :
              </p>
              <ul>
                <li>
                  <a
                    href="https://canva.link/elc8ib758u9b9sq"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold underline underline-offset-2 hover:text-primary"
                  >
                    Launch Graphic templates
                  </a>{" "}
                  — use these to announce your new site on social.
                </li>
                <li>
                  <a
                    href="https://docs.google.com/document/u/1/d/14o7wklEINEH-cnjE-QzvshtT7Hdb-qpQMeS-fPsPUZc/copy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold underline underline-offset-2 hover:text-primary"
                  >
                    Launch Promo posts and emails
                  </a>{" "}
                  — make a copy and customize for your audience.
                </li>
              </ul>
              <p>Now get out there and share, share, share!</p>
            </Section>

            <Section id="20-places" title="20 Places You Should Be Showing Off Your Website" index={1}>
              <p>
                Now you've got a shiny new website. Which is the hard part, so
                you should be super stoked!!
              </p>
              <p>
                But a website only works if people are actually seeing it.
                AKA... traffic.
              </p>
              <p>
                Here are 20 places you should be showing off your website (not
                all-inclusive, but a good getting-started list!):
              </p>
              <ol>
                <li>Instagram link in bio</li>
                <li>LinkedIn contact info</li>
                <li>Business email signature</li>
                <li>Personal email signature</li>
                <li>Business card</li>
                <li>Facebook profile / about section</li>
                <li>Facebook business page</li>
                <li>Pinterest profile</li>
                <li>YouTube description</li>
                <li>Podcast show notes</li>
                <li>Client proposals</li>
                <li>Invoices</li>
                <li>Welcome emails</li>
                <li>Lead magnet thank-you page</li>
                <li>Online directories</li>
                <li>Google Business Profile</li>
                <li>Speaking / event bios</li>
                <li>Guest blog bios</li>
                <li>Media kit</li>
                <li>QR code on printed materials</li>
              </ol>
              <p>
                Start with one more today than you had yesterday. Then share in
                another place tomorrow!
              </p>
            </Section>

            <Section id="requesting-updates" title="Requesting Website Updates" index={2}>
              <p>
                Don't forget: Your Website Concierge includes a set number of
                website updates or edits each month.
              </p>
              <p>
                Updates are counted on a monthly calendar basis (example: you
                get your monthly updates in September, and a fresh set in
                October). Unused updates do not roll over month to month.
              </p>
              <p>
                <strong>How to request your website updates:</strong>
              </p>
              <ol>
                <li>
                  Email{" "}
                  <a href="mailto:hello@threedaylaunch.com">
                    hello@threedaylaunch.com
                  </a>{" "}
                  with your update request. Please include as much detail as
                  possible (the more detail the better so we can understand
                  exactly what you need!).
                </li>
                <li>
                  You will receive a confirmation email within 24 business
                  hours letting you know we've received the update request and
                  will be working on it for you. If we need any clarification
                  or have questions about the update, we'll ask you.
                </li>
                <li>
                  Within 24 business hours from receiving that confirmation
                  email, your update will be complete.
                </li>
              </ol>
            </Section>

            <Section id="adding-blog-posts" title="Adding Blog Posts to Your Website" index={3}>
              <p>
                Blogging is not only a great way to connect with your audience,
                it's also so important for SEO (search engine optimization).
                Google loves seeing new content added to your website on the
                regular. So when it comes to "how much should I blog?"
              </p>
              <p>
                The answer is... the more the merrier! (As long as you are
                posting quality, helpful content 😉).
              </p>
              <p>Here's how to add new blog posts to your website:</p>
              <ol>
                <li>
                  Sign in to your website at your blog admin login. This link
                  will be your domain <code>/admin/login</code>. For example:{" "}
                  <code>yourwebsitename.com/admin/login</code> (if you have a
                  .org or .co make sure to change the .com!).
                </li>
                <li>
                  Click <strong>"First time? Create your account"</strong> →
                  sign up with your email (the email you're using right now is
                  the one we've granted admin access to; others cannot sign up
                  to have admin access).
                </li>
                <li>
                  After signing on, you'll be at the blog admin. This lets you
                  see all drafts and published posts. To create a new post,
                  click <strong>"New Post"</strong>. You can use the block
                  inserts to add paragraphs, images, and headings into your
                  post.
                </li>
              </ol>
              <p>
                If you'd rather have us publish a blog post for you, we can
                absolutely do that as one of your monthly website updates (if
                your plan includes updates). More on your monthly updates in the
                Requesting Website Updates section above.
              </p>
            </Section>

            <Section
              id="seo-blog-review"
              title="Your Monthly SEO Blog Post"
              index={4}
            >
              <p>
                If your Website Concierge includes done-for-you blogging, you will receive each blog post for your review as a draft (not yet published or visible). This is so you have the opportunity to review and make any desired wording adjustments before hitting publish. 
              </p>
              <p>
                How to review and publish your done-for-you blog posts:
              </p>
              <ol>
                <li>
                  Log on to your website blog admin (
                  <code>yourwebsitedomain.com/admin</code>&nbsp;)
                </li>
                <li>
                  At the top of your blog admin page, you will see the newest
                  done-for-you blog post saved as a draft. Click{" "}
                  <strong>"Edit"</strong> to read and review the post.
                </li>
                <li>
                  Your job is to tweak any wording that doesn't sound like you.
                  This may be phrases you wouldn't normally say, or ideas that
                  don't quite mesh with your brand. The goal is that the
                  article sounds like you talking 1:1 with a client or customer
                  (not like Sierra! 😉).
                </li>
                <li>
                  Then, click <strong>Publish</strong> to set the post live!
                </li>
              </ol>
              <p>
                Tips for review:
              </p>
              <ul>
                <li>
                  Throughout the blog post, you'll see important SEO keywords
                  from your SEO Content Map woven into the writing. The primary
                  SEO keyword will be in the title, the meta description, and
                  the post URL — all critical for our SEO efforts. I recommend
                  you do not change the blog post title, link, or SEO meta
                  description for this reason.
                </li>
                <li>
                  You will also see sample specific stories, like a personal
                  experience or a client example, that are made up. These are
                  intended as prompts for you to add in a real personal or
                  client story in that place. Google loves personal experience
                  and expertise in our age of AI-generated fluff, so adding in
                  these specific, personal touches really helps our SEO
                  efforts.
                </li>
                <li>
                  You will see multiple places where we link to other blog
                  posts and pages on the website. These "internal links" are
                  helpful for SEO as well. Feel free to add more internal links
                  if there are others you feel would be helpful to readers.
                </li>
              </ul>
              <p>
                {"\n"}
              </p>
            </Section>



            <Section id="website-analytics-dashboard" title="Website Analytics Dashboard" index={5}>
              <p>
                Your custom analytics dashboard gives you anytime access to helpful information about how your website is performing.
              </p>
              <p>
                At Three Day Launch, we want your website to serve as an actionable business tool — not just a pretty digital business card. Reviewing your website data over time helps us identify opportunities, make strategic improvements, and keep your website moving in the right direction.
              </p>
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-2">
                How to Access Your Dashboard
              </h3>
              <ol>
                <li>
                  Visit <code>yourbusiness.com/admin/login</code> — replace “yourbusiness.com” with your actual website domain and enter the complete address into your browser.
                </li>
                <li>
                  Log in using the email address.
                </li>
              </ol>
              <p>
                For your website’s security, only approved email addresses can access the dashboard. This is the same login you use for your Blog Post admin. If you have forgotten your password or have not created one yet, select the reset password option.
              </p>
              <ol start={3}>
                <li>
                  Select the <strong>Analytics</strong> tab at the top of your screen.
                </li>
                <li>
                  Explore your dashboard to view your latest website traffic, user activity, form submissions, and other helpful insights.
                </li>
              </ol>
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-2">
                Understanding Your Website Data
              </h3>
              <p>
                Red numbers do not automatically mean something is wrong. Website traffic and SEO naturally fluctuate in waves, so small increases and decreases from month to month are completely normal.
              </p>
              <p>
                Think of your analytics dashboard as a website health check, not a list of problems. Our goal is to monitor trends over time, make strategic adjustments when needed, and continue strengthening your website.
              </p>
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-2">
                What to Expect From a New Dashboard
              </h3>
              <p>
                When your dashboard is first activated, there may not be enough search data to show meaningful results yet. This is completely normal for a new website or a newly connected dashboard.
              </p>
              <p>
                As your website gains visibility in Google and collects more visitor data, your dashboard will give us more information to track, compare, and build from.
              </p>
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-2">
                Personalized Recommendations From Sierra
              </h3>
              <p>
                Once we have enough data to review, I’ll begin adding custom recommendations directly to your dashboard each month based on what we’re seeing. These recommendations come personally from me, Sierra, after I review your website’s performance.
              </p>
              <p>
                When you’re ready to move forward with a recommendation, email{" "}
                <a href="mailto:hello@threedaylaunch.com">
                  hello@threedaylaunch.com
                </a>{" "}
                and let us know which update you would like us to make. We’ll take it from there!
              </p>
            </Section>

            <Section id="free-website-care" title="Free Website Care: Here's How" index={6}>
              <p>{"\n"}</p>
              <p>
                We offer free website care to clients like you.
              </p>
              <p>
                Here's how to snag a free month of website care (or many months,
                up to you!):
              </p>
              <p>
                Refer your colleague, friend, or fellow business owner to us
                for their own website of their dreams. You can shoot an email
                connecting us, or just send them to{" "}
                <a
                  href="https://threedaylaunch.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-primary"
                >
                  my website
                </a>.
              </p>
              <p>
                When your referee launches their new online home, I'll grant a
                free 4 weeks of website care to your account.
              </p>
              <p>
                No special code or link needed — just make sure they tell me
                you sent them 🙂 They'll get a special friends-of-friends deal
                too.
              </p>
            </Section>


            <Section id="leave-a-review" title="A Favor to Ask" index={7}>
              <p>
                I have a small favor to ask: would you be willing to share your
                experience working together? A short review about what it was
                like to build your website with me means the world.
              </p>
              <p>
                Not only does this help me a TON if you are willing (😉), it
                also allows me to publish a fun blog post to my own website
                with some of the details of your own launch, linking back to
                your site and resources of course! If this is something you
                would be open to, I would LOVE to have your feedback.
              </p>
              <div className="mt-10">
                <ReviewForm />
              </div>
            </Section>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function Section({
  id,
  title,
  index,
  children,
}: {
  id: string;
  title: string;
  index: number;
  children: React.ReactNode;
}) {
  const isCard = index % 2 === 1;
  return (
    <section
      id={id}
      className={cn(
        "scroll-mt-24",
        isCard &&
          "rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm"
      )}
    >
      <h2 className="text-2xl md:text-3xl font-bold text-foreground">{title}</h2>
      <div className="prose prose-neutral mt-5 max-w-none text-foreground/85 [&_a]:text-primary [&_a]:underline [&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-sm [&_p]:leading-relaxed [&_li]:leading-relaxed [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_p]:mt-4 [&_ol]:mt-4 [&_ul]:mt-4 [&_li]:mt-1">
        {children}
      </div>
    </section>
  );
}
